import { IBooleanCondition } from '../inputs';
import { ExpressionType } from '../schema/expressions/IExpression';
import { mapReferenceToPath } from '../schema/expressions/evaluate/mapReferenceToPath';
import { IReference } from '../schema/expressions/references/IReference';
import { collectExpressionReferences } from '../schema/expressions/references/collectExpressionReferences';
import { ConditionGroupOperator } from '../schema/expressions/repository/condition-group/IConditionGroupExpression';
import { IParentWidgetScope } from '../schema/widgets/IWidgetType';
import { Widget } from '../schema/widgets/Widget';

export type IConditionData = ReturnType<typeof WidgetConditionManager.merge>;

export class WidgetConditionManager {
  /**
   * Unisce gli scope del parent con il widget per
   * avere tutte le condizioni relative al field (visible o enabled)
   */
  static mergeScope<W extends Widget>(
    widget: W,
    field: 'visible' | 'enabled',
    parentScope: IParentWidgetScope = [],
  ) {
    const filterNotNull = (
      s: IBooleanCondition | undefined,
    ): s is NonNullable<IBooleanCondition> => Boolean(s);

    return [
      ...parentScope.map(s => s[field]),
      widget[field] as IBooleanCondition | undefined,
    ].filter(filterNotNull);
  }

  /**
   * shortcut per costruire il risultato della merge
   */
  private static buildResult(
    condition: NonNullable<IBooleanCondition>,
    paths: string[] = [],
    references: IReference[] = [],
  ) {
    return {
      condition,
      paths,
      references,
    };
  }

  static getConditionData(condition: IBooleanCondition) {
    if (!condition) return null;
    if (condition.type === 'static') {
      return this.buildResult({
        type: 'static' as const,
        value: condition.value,
      });
    }

    const references = collectExpressionReferences(condition.expression);

    const paths = references.map(r => mapReferenceToPath(r));

    return this.buildResult(condition, paths, references);
  }

  /**
   * Valuta una condizione e ritorna i dati necessari per la valutazione
   * (path, references,...)
   */
  static merge(rawConditions: Array<IBooleanCondition | undefined>) {
    // Creo la lista di condizioni da valutare
    const conditions = rawConditions.filter(
      (c): c is NonNullable<IBooleanCondition> => c != null,
    );

    // Se tutte le condizioni non esistono, torno null
    if (conditions.length === 0) return null;

    // Se almeno una condizione è statica e false,
    if (conditions.some(s => s.type === 'static' && s.value === false)) {
      return this.buildResult({ type: 'static' as const, value: false });
    }

    // Se tutte le condizioni sono statiche e true, ritorno la condizione true
    if (conditions.every(s => s.type === 'static' && s.value === true)) {
      return this.buildResult({ type: 'static' as const, value: true });
    }

    // Scope dinamico, ovvero con solo le condizioni generate dalle espressioni
    const dynamicScope = conditions.filter(
      s => Boolean(s) && s?.type === 'expression',
    ) as Array<IBooleanCondition & { type: 'expression' }>;

    // Altriemnti costruisco i dati della condizione
    const expressions = dynamicScope.map(s => s.expression);

    const references = expressions.flatMap(expr =>
      collectExpressionReferences(expr),
    );
    const paths = references.map(r => mapReferenceToPath(r));

    return this.buildResult(
      {
        type: 'expression',
        expression: {
          type: ExpressionType.ConditionGroup,
          operator: ConditionGroupOperator.And,
          conditions: expressions,
        },
      },
      paths,
      references,
    );
  }

  /**
   * Recupera la configurazione di una condizione specifica (editable o visible) di un widget.
   * Aggiunge alla condizione del widget le condizioni dei parent.
   */
  static mergeWithScope<W extends Widget>(
    widget: W,
    field: 'visible' | 'enabled',
    parentScope: IParentWidgetScope = [],
  ) {
    const scope = this.mergeScope(widget, field, parentScope);
    return this.merge(scope);
  }
}
